import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  SquaresFour, Images, MapTrifold, ClockCounterClockwise, AirplaneTilt,
  SealCheck, UploadSimple, MagnifyingGlass, SignOut, Planet, Plugs,
  BookOpenText, Leaf, List, X, UserCircle, Stack, IdentificationCard, Archive, CalendarStar,
  GlobeHemisphereWest, Binoculars, UsersThree, Broadcast, Code, Envelope, Lightning,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import InstallPWA from "@/components/InstallPWA";
import { Input } from "@/components/ui/input";

const NAV = [
  { to: "/dashboard", label: "Control Center", icon: SquaresFour, end: true },
  { to: "/library", label: "Photo Library", icon: Images },
  { to: "/my-uploads", label: "My Uploads", icon: Stack },
  { to: "/map", label: "Map View", icon: MapTrifold },
  { to: "/atlas", label: "Living Atlas", icon: GlobeHemisphereWest },
  { to: "/timeline", label: "Timeline", icon: ClockCounterClockwise },
  { to: "/trips", label: "Trips & Clusters", icon: AirplaneTilt },
  { to: "/stories", label: "Location Stories", icon: BookOpenText },
  { to: "/nature", label: "Species Guide", icon: Leaf },
  { to: "/observations", label: "Nature Observations", icon: Binoculars },
  { to: "/photographers", label: "Photographers", icon: UserCircle },
  { to: "/inbox", label: "Inbox", icon: Envelope },
  { to: "/review", label: "Review Queue", icon: SealCheck },
  { to: "/upload", label: "Import Photos", icon: UploadSimple },
  { to: "/events", label: "Event Albums", icon: CalendarStar },
  { to: "/event-sets", label: "Event Photo Sets", icon: Broadcast },
  { to: "/albums", label: "Shared Albums", icon: UsersThree },
  { to: "/ingest", label: "Bulk Ingest", icon: Archive },
  { to: "/api", label: "Cross-Site API", icon: Plugs },
  { to: "/embeds", label: "Embed Widgets", icon: Code },
  { to: "/webhooks", label: "Webhook Console", icon: Lightning },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="grid h-9 w-9 place-items-center rounded-md bg-[#F27A54] text-white">
        <Planet size={20} weight="fill" />
      </div>
      <div className="leading-tight">
        <p className="font-display text-sm font-extrabold tracking-tight">PHOTO<span className="font-black">PULSE</span>.pro<span className="align-super text-[8px]">™</span></p>
        <p className="overline text-[8px] text-muted-foreground">Capture · Enrich · Elevate</p>
      </div>
    </div>
  );
}

function NavItems({ onNavigate }) {
  return (
    <>
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
            }`
          }
        >
          <n.icon size={18} weight="bold" />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/search?q=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-border bg-white lg:flex">
        <div className="border-b border-border px-5 py-4"><Brand /></div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3"><NavItems /></nav>
        <div className="border-t border-border p-3">
          <NavLink to="/profile" data-testid="nav-profile"
            className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"}`}>
            <IdentificationCard size={18} weight="bold" /> My Profile
          </NavLink>
          <div className="mb-2 px-2">
            <p className="truncate text-xs font-semibold text-zinc-800">{user?.name || user?.email}</p>
            <p className="overline text-[8px] text-muted-foreground">{user?.role}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-600 transition-colors duration-200 hover:bg-red-50 hover:text-red-600">
            <SignOut size={18} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:ml-60">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-white/80 px-4 py-3 backdrop-blur-xl sm:px-6">
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-border text-zinc-700 lg:hidden">
            <List size={20} weight="bold" />
          </button>
          <div className="lg:hidden"><Brand /></div>
          <form onSubmit={submitSearch} className="relative ml-auto w-full max-w-md lg:ml-0">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search photos…"
              className="h-9 rounded-md border-border pl-9 text-sm"
            />
          </form>
          <InstallPWA />
          <NotificationsBell />
        </header>
        <main className="p-4 sm:p-6">{children}</main>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-4">
              <Brand />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="grid h-8 w-8 place-items-center rounded-md hover:bg-zinc-100"><X size={18} weight="bold" /></button>
            </div>
            <nav className="flex-1 space-y-0.5 overflow-y-auto p-3"><NavItems onNavigate={() => setOpen(false)} /></nav>
            <div className="border-t border-border p-3">
              <NavLink to="/profile" onClick={() => setOpen(false)}
                className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                  isActive ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
                <IdentificationCard size={18} weight="bold" /> My Profile
              </NavLink>
              <div className="mb-2 px-2">
                <p className="truncate text-xs font-semibold text-zinc-800">{user?.name || user?.email}</p>
                <p className="overline text-[8px] text-muted-foreground">{user?.role}</p>
              </div>
              <button onClick={logout} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-red-50 hover:text-red-600">
                <SignOut size={18} weight="bold" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
